// ADR 6.9.26j §4 — what a sign-in costs the browser that pays it, as
// arithmetic with no I/O in it.
//
// 5.9.26p §2 states argon2id at ~1–2 s and proof-of-work at ~0.5 s (difficulty
// 18) to ~2–5 s (difficulty 22). The server already shows which rung of the
// ladder it is serving (`auth_pow_challenges_issued_total{difficulty}`);
// nothing shows what that rung costs a browser, and the browser is where the
// ladder is felt — a 2018 laptop at difficulty 22 is a sign-in nobody completes.
//
// **The arithmetic lives here rather than in `credentials.ts` for a reason
// 6.9.26c already hit.** Testing AC6 through `submitCredentials` alone would
// mean msw plus a real argon2 derivation for every case — slow, and testing the
// wrong thing. This is the same split 6.9.26c made for `bundleBudget.ts`, and
// 5.9.26i before it for `readEvents` / `streamEvents`: the arithmetic half
// testable without the expensive half.

/** The one effect this module has, injected so a test can drive it. */
export interface Clock {
  now(): number
}

/**
 * `performance.now()` where it exists, `Date.now()` otherwise.
 *
 * A monotonic source matters here: these are durations, and a wall clock that
 * steps backwards mid-argon2 would report a negative second.
 */
export const systemClock: Clock = {
  now: () => (typeof performance === 'undefined' ? Date.now() : performance.now()),
}

/**
 * The transitions the timer measures between.
 *
 * Named for the boundary rather than for `CredentialStage`, which lives in
 * `auth/credentials.ts` — importing it would point `telemetry/` at `auth/` for
 * a string union, and this module has no other reason to know that door exists.
 */
export type CostMark = 'proving' | 'hashing' | 'done'

/** The two numbers, which are only meaningful together. */
export interface StageTimings {
  proving_ms: number
  hashing_ms: number
}

export interface StageTimer {
  /** Record that `mark` has just been reached. */
  mark(mark: CostMark): void
  /** The spans between the marks recorded so far. */
  timings(): StageTimings
}

/** Round to whole milliseconds — `performance.now()` is sub-millisecond. */
function span(from: number | undefined, to: number | undefined): number {
  if (from === undefined || to === undefined) return 0
  return Math.max(0, Math.round(to - from))
}

/**
 * A timer over a sequence of marks.
 *
 * An unreached mark yields `0` rather than `undefined` or `NaN`: the event
 * fires on the failure path too (`outcome: "failed"`), and a submit that threw
 * before `hashing` began genuinely paid nothing for it. A `NaN` would arrive at
 * PostHog as a property nothing can aggregate.
 */
export function createStageTimer(clock: Clock = systemClock): StageTimer {
  const at = new Map<CostMark, number>()
  return {
    mark(mark) {
      at.set(mark, clock.now())
    },
    timings() {
      return {
        proving_ms: span(at.get('proving'), at.get('hashing')),
        hashing_ms: span(at.get('hashing'), at.get('done')),
      }
    },
  }
}
